/**
 * Pretty-prints a Firebase callable error.
 *
 * When a callable throws `HttpsError('invalid-argument', 'Settings payload invalid', { issues })`
 * the SDK wraps it in a `FirebaseError` with a `details` field that
 * still contains the Zod issue list — but the default `.message` is
 * just the top-level string. This helper digs into `details.issues`
 * and turns them into a human-readable summary like
 *
 *     Settings payload invalid.
 *     • twilio.phoneNumber: must be E.164 format or empty
 *     • telnyx.connectionId: must contain at least 3 chars
 */
interface CallableErrorShape {
  message?: string;
  details?: {
    issues?: Array<{
      path?: Array<string | number>;
      message?: string;
    }>;
    message?: string;
  };
}

export function formatCallableError(err: unknown): string {
  const e = err as CallableErrorShape;
  const baseMessage =
    typeof e?.message === 'string' && e.message.length > 0
      ? e.message
      : 'Unbekannter Fehler.';
  const issues = e?.details?.issues;
  if (!Array.isArray(issues) || issues.length === 0) {
    return baseMessage;
  }
  const lines = issues.slice(0, 6).map((issue) => {
    const path = (issue.path ?? []).join('.');
    const msg = issue.message ?? 'Validation failed';
    return path ? `• ${path}: ${msg}` : `• ${msg}`;
  });
  if (issues.length > 6) {
    lines.push(`• … ${issues.length - 6} weitere`);
  }
  return [baseMessage, ...lines].join('\n');
}
